import api from '../../services/api';
import { Product, ProductVariant } from '../../types/pz';
import { invalidateProductSelectionCatalog } from './productSelectionApi';

export interface ProductListFilters {
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE' | null;
  category?: string | null;
  productCategoryId?: string | null;
}

/**
 * Karta produktu form (owner decision 2026-09-23). Only the fields the card
 * shows are sent; everything else (legacy default prices, country of origin,
 * classification, description, legacy package footprint) stays untouched on
 * the server because it is absent from the payload.
 */
export interface ProductFormValues {
  name: string;
  productCategoryId?: string | null;
  ean?: string | null;
  cnCode?: string | null;
  englishName?: string | null;
  isActive?: boolean;
  length?: number | null;
  width?: number | null;
  thickness?: number | null;
  /** Legacy plane dimension; only sent when the product already uses it. */
  height?: number | null;
  /** Weight of ONE piece in kg. */
  grossWeight?: number | null;
  /** "Pakowane w paczki" — the pallet counts packages. */
  palletCountsPackages?: boolean;
  piecesPerPackage?: number | null;
  palletUnitsPerPallet?: number | null;
  palletLength?: number | null;
  palletWidth?: number | null;
  palletEmptyWeight?: number | null;
  palletStackLayers?: number | null;
  averagePurchasePricePLN?: number | null;
  averagePurchasePriceUnit?: 'pcs' | 'm2' | 'm3' | null;
  /** Real-variant average-cost overrides keyed by variant id; null inherits. */
  variantCosts?: Record<string, number | null | undefined>;
}

export interface ProductPayloadOptions {
  isNew?: boolean;
  /** The viewer may edit the average cost (ADMIN/OPERATIONS). */
  includeAverageCost?: boolean;
  /** Real variants whose cost override is sent (all loaded real variants). */
  variantIds?: string[];
}

export interface VariantFormValues {
  ean?: string | null;
  attributes: Record<string, string>;
  isActive?: boolean;
}

const nullable = <T,>(value: T | undefined | null | ''): T | null => (
  value === undefined || value === null || value === '' ? null : value as T
);

// Exported so the whitelist can be unit-tested directly: a field missing here
// is silently dropped from every save regardless of what the form displays.
export function buildProductPayload(values: ProductFormValues, options: ProductPayloadOptions = {}) {
  const countsPackages = values.palletCountsPackages === true;
  const payload: Record<string, unknown> = {
    name: values.name,
    productCategoryId: values.productCategoryId ?? null,
    ean: values.ean || null,
    cnCode: values.cnCode || null,
    englishName: values.englishName || null,
    isActive: values.isActive !== false,
    length: nullable(values.length),
    width: nullable(values.width),
    thickness: nullable(values.thickness),
    grossWeight: nullable(values.grossWeight),
    palletCountsPackages: countsPackages,
    // A loose pallet has no paczka: clear a stale value so it cannot
    // silently return when "Pakowane w paczki" is switched on again.
    piecesPerPackage: countsPackages ? nullable(values.piecesPerPackage) : null,
    palletUnitsPerPallet: nullable(values.palletUnitsPerPallet),
    palletLength: nullable(values.palletLength),
    palletWidth: nullable(values.palletWidth),
    palletEmptyWeight: nullable(values.palletEmptyWeight),
    palletStackLayers: nullable(values.palletStackLayers),
  };
  if (values.height !== undefined) payload.height = nullable(values.height);
  // Classification is no longer shown; new products keep the historical default.
  if (options.isNew) payload.category = 'FINISHED_GOOD';
  if (options.includeAverageCost) {
    const price = nullable(values.averagePurchasePricePLN);
    payload.averagePurchasePricePLN = price;
    payload.averagePurchasePriceUnit = price === null ? null : (values.averagePurchasePriceUnit ?? null);
    if (options.variantIds && options.variantIds.length > 0) {
      payload.variantAverageCosts = options.variantIds.map((variantId) => ({
        variantId,
        averagePurchasePricePLN: nullable(values.variantCosts?.[variantId]),
      }));
    }
  }
  return payload;
}

export function buildVariantPayload(productId: string, values: VariantFormValues) {
  return {
    productId,
    ean: values.ean || null,
    attributes: values.attributes,
    isActive: values.isActive !== false,
  };
}

export async function fetchProducts(filters: ProductListFilters = {}) {
  const params = new URLSearchParams({ includeVariants: 'true' });
  if (filters.search) params.set('search', filters.search);
  if (filters.category) params.set('category', filters.category);
  if (filters.productCategoryId) params.set('productCategoryId', filters.productCategoryId);
  if (filters.status) params.set('isActive', String(filters.status === 'ACTIVE'));

  const response = await api.get<Product[]>(`/products?${params.toString()}`);
  return response.data;
}

export async function fetchProduct(id: string) {
  const response = await api.get<Product>(`/products/${id}?includeVariants=true`);
  return response.data;
}

/**
 * Returns the FIXED enum classification values (RAW_MATERIAL/FINISHED_GOOD/…),
 * shown in the UI as "Klasyfikacja". This is NOT the user-managed taxonomy — for
 * that ("Kategoria": Płyta OSB, Regips…) use
 * `fetchProductCategoryTags` in ./productCategoriesApi.
 */
export async function fetchProductCategories() {
  const response = await api.get<string[]>('/products/categories');
  return response.data;
}

export async function createProduct(values: ProductFormValues, options: ProductPayloadOptions = {}) {
  const response = await api.post<Product>('/products', buildProductPayload(values, { ...options, isNew: true }));
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function updateProduct(id: string, values: ProductFormValues, options: ProductPayloadOptions = {}) {
  const response = await api.put<Product>(`/products/${id}`, buildProductPayload(values, { ...options, isNew: false }));
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function deleteProduct(id: string) {
  await api.delete(`/products/${id}`);
  invalidateProductSelectionCatalog();
}

export async function permanentlyDeleteProduct(id: string) {
  await api.delete(`/products/${id}/permanent`);
  invalidateProductSelectionCatalog();
}

export async function createVariant(productId: string, values: VariantFormValues) {
  const response = await api.post<ProductVariant>(
    `/products/${productId}/variants`,
    buildVariantPayload(productId, values)
  );
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function updateVariant(productId: string, variantId: string, values: VariantFormValues) {
  const response = await api.put<ProductVariant>(
    `/products/variants/${variantId}`,
    buildVariantPayload(productId, values)
  );
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function deleteVariant(variantId: string) {
  await api.delete(`/products/variants/${variantId}`);
  invalidateProductSelectionCatalog();
}
